import React, { useId, useState } from 'react';

import { Input } from '@flixbus/honeycomb-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import GroupValidationMessage from './GroupValidationMessage';
import { combineDateTime, splitTimestamp, toDateOnly } from '../utils/incidentTime';
import { isAtOrAfter } from '../utils/timestampOrder';

import styles from './OriginalDriverCushioningInputs.module.css';

const COPY = CREATE_BREAKDOWN_FLOW_TEXT.originalDriverCushioning;
const ERR = CREATE_BREAKDOWN_FLOW_TEXT.errors;

/**
 * "Original driver cushioning parameters" — section label + two split
 * date+time pairs (Travelling start / Travelling end). Driver is implicitly
 * the assignment's original driver; the payload mapper resolves the id.
 *
 * Enforces the cushion sub-chain against the flow's incident baseline:
 *  - start pair flags red when `start < incident` (baseline).
 *  - end pair flags red when `end < start` (or `end < incident` when start
 *    is empty — falls back to baseline).
 *
 * `incidentTimestamp` is optional; when absent, incident-baseline checks
 * are skipped and only the intra-pair order (`end >= start`) still fires.
 *
 * Emits `{ fromTimestamp, toTimestamp }` as combined `YYYY-MM-DDTHH:mm` so
 * the leaves' payload stays shape-stable.
 *
 * @param {{
 *   value: { fromTimestamp: string, toTimestamp: string },
 *   incidentTimestamp?: string,
 *   onChange: (next: { fromTimestamp: string, toTimestamp: string }) => void,
 * }} props
 */
const OriginalDriverCushioningInputs = ({ value, incidentTimestamp = '', onChange }) => {
	const startDateId = useId();
	const startTimeId = useId();
	const endDateId = useId();
	const endTimeId = useId();

	const [initialStartDate, initialStartTime] = splitTimestamp(value?.fromTimestamp);
	const [initialEndDate, initialEndTime] = splitTimestamp(value?.toTimestamp);
	const [fromDate, setFromDate] = useState(initialStartDate);
	const [fromTime, setFromTime] = useState(initialStartTime);
	const [toDate, setToDate] = useState(initialEndDate);
	const [toTime, setToTime] = useState(initialEndTime);

	const emitChange = (nextFromDate, nextFromTime, nextToDate, nextToTime) => {
		onChange({
			fromTimestamp: combineDateTime(nextFromDate, nextFromTime),
			toTimestamp: combineDateTime(nextToDate, nextToTime),
		});
	};

	const handleFromDate = (event) => {
		const next = event.target.value;
		setFromDate(next);
		emitChange(next, fromTime, toDate, toTime);
	};
	const handleFromTime = (event) => {
		const next = event.target.value;
		setFromTime(next);
		emitChange(fromDate, next, toDate, toTime);
	};
	const handleToDate = (event) => {
		const next = event.target.value;
		setToDate(next);
		emitChange(fromDate, fromTime, next, toTime);
	};
	const handleToTime = (event) => {
		const next = event.target.value;
		setToTime(next);
		emitChange(fromDate, fromTime, toDate, next);
	};

	const currentFrom = combineDateTime(fromDate, fromTime);
	const currentTo = combineDateTime(toDate, toTime);

	// Start pair — invalid when start precedes incident.
	const startBeforeIncident =
		!!currentFrom && !!incidentTimestamp && !isAtOrAfter(currentFrom, incidentTimestamp);

	// End pair — invalid when it fails the order. Compare against start when
	// set, otherwise fall back to incident baseline.
	const endBeforeStart = !!currentFrom && !!currentTo && !isAtOrAfter(currentTo, currentFrom);
	const endBeforeIncident =
		!currentFrom &&
		!!currentTo &&
		!!incidentTimestamp &&
		!isAtOrAfter(currentTo, incidentTimestamp);
	const endInvalid = endBeforeStart || endBeforeIncident;

	const startGroupError = startBeforeIncident ? ERR.cushionStartBeforeIncident : undefined;
	const endGroupError = endInvalid ? ERR.cushionEndBeforeStart : undefined;

	// Native date-picker floor: start >= incident; end >= start (or incident).
	const startMin = toDateOnly(incidentTimestamp);
	const endMin = toDateOnly(currentFrom) || toDateOnly(incidentTimestamp);

	return (
		<div className={styles.section}>
			<p className={styles.sectionLabel}>{COPY.sectionLabel}</p>

			<div className={styles.row}>
				<div className={styles.pairWithError}>
					<div className={styles.dateTimePair}>
						<div className={styles.dateField}>
							<Input
								id={startDateId}
								type="date"
								label={COPY.startsLabel}
								value={fromDate}
								onChange={handleFromDate}
								min={startMin}
								valid={startBeforeIncident ? false : undefined}
								data-testid="original-cushioning-start-date-input"
							/>
						</div>
						<div className={styles.timeField}>
							<Input
								id={startTimeId}
								type="time"
								aria-label={COPY.startsTimeAriaLabel}
								value={fromTime}
								onChange={handleFromTime}
								valid={startBeforeIncident ? false : undefined}
								data-testid="original-cushioning-start-time-input"
							/>
						</div>
					</div>

					<GroupValidationMessage
						message={startGroupError}
						testId="original-cushioning-start-group-error"
					/>
				</div>

				<div className={styles.pairWithError}>
					<div className={styles.dateTimePair}>
						<div className={styles.dateField}>
							<Input
								id={endDateId}
								type="date"
								label={COPY.endsLabel}
								value={toDate}
								onChange={handleToDate}
								min={endMin}
								valid={endInvalid ? false : undefined}
								data-testid="original-cushioning-end-date-input"
							/>
						</div>
						<div className={styles.timeField}>
							<Input
								id={endTimeId}
								type="time"
								aria-label={COPY.endsTimeAriaLabel}
								value={toTime}
								onChange={handleToTime}
								valid={endInvalid ? false : undefined}
								data-testid="original-cushioning-end-time-input"
							/>
						</div>
					</div>

					<GroupValidationMessage
						message={endGroupError}
						testId="original-cushioning-end-group-error"
					/>
				</div>
			</div>
		</div>
	);
};

export default OriginalDriverCushioningInputs;
